/**
 * Eigenes CSS: Editor + Live-Vorschau. Die Vorschau läuft über den ThemeManager (useThemePreview), der das
 * CSS bereinigt (lib/cssSanitizer) in <style id="lf-user-css"> nach den Basis-Styles setzt – die Beispiel-
 * Nachrichtenkarte zeigt die Wirkung sofort. Gespeichert wird per PATCH /users/@me/settings.
 */
import { useEffect, useId, useMemo, useState } from 'react';
import { sanitizeCss } from '../../lib/cssSanitizer';
import { useAppStore } from '../../store/app';
import { toast } from '../../store/ui';
import { formatBytes } from './ImageUpload';
import { mergePreview, patchSettings } from './useSettings';

const byteLength = (s: string) => new TextEncoder().encode(s).length;

/** Normalisierter Vergleich: Hat die Bereinigung inhaltlich etwas entfernt? */
export function cssWasChanged(raw: string, clean: string): boolean {
  const norm = (s: string) => s.replace(/\/\*[\s\S]*?(\*\/|$)/g, '').replace(/\s+/g, '');
  return norm(raw) !== norm(clean);
}

export function CustomCssEditor() {
  const saved = useAppStore((s) => s.settings?.customCss ?? '');
  const maxBytes = useAppStore((s) => s.limits.maxCustomCssBytes);
  const [draft, setDraft] = useState(saved);
  const [live, setLive] = useState(true);
  const [busy, setBusy] = useState(false);
  const inputId = useId();
  const clean = useMemo(() => sanitizeCss(draft), [draft]);
  const changed = draft.trim() !== '' && cssWasChanged(draft, clean);
  const bytes = byteLength(draft);
  const tooBig = maxBytes > 0 && bytes > maxBytes;
  const dirty = draft !== saved;

  useEffect(() => {
    if (live && dirty) mergePreview({ customCss: clean });
    else mergePreview({}, ['customCss']);
  }, [clean, live, dirty]);
  useEffect(() => () => mergePreview({}, ['customCss']), []);

  const apply = async () => {
    if (tooBig) return;
    setBusy(true);
    const res = await patchSettings({ customCss: draft.trim() ? draft : null });
    setBusy(false);
    if (res) toast.success('Eigenes CSS übernommen');
  };

  return (
    <section className="lf-settings-card stack" aria-labelledby="css-heading" data-testid="custom-css">
      <h2 id="css-heading">Eigenes CSS</h2>
      <p className="small muted">
        Überschreibe Variablen wie <code>--lf-accent</code> oder gestalte Elemente um. Externe Stylesheets (<code>@import</code>), Skript-URLs und
        unsichere Konstrukte werden entfernt; Bilder nur über https oder eigene Medien.
      </p>
      <div className="lf-css-editor">
        <div className="field">
          <label htmlFor={inputId}>CSS</label>
          <textarea
            id={inputId}
            className="textarea lf-code-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            spellCheck={false}
            rows={12}
            placeholder={':root {\n  --lf-accent: #e0a030;\n}\n.lf-sample-message { border-radius: 14px; }'}
            aria-invalid={tooBig}
            aria-describedby={`${inputId}-hint`}
            data-testid="custom-css-input"
            onKeyDown={(e) => {
              // Tab fügt Einrückung ein statt den Fokus zu verlassen (Shift+Tab verlässt das Feld).
              if (e.key === 'Tab' && !e.shiftKey) {
                e.preventDefault();
                const el = e.currentTarget;
                const { selectionStart: a, selectionEnd: b } = el;
                const next = `${draft.slice(0, a)}  ${draft.slice(b)}`;
                setDraft(next);
                requestAnimationFrame(() => el.setSelectionRange(a + 2, a + 2));
              }
            }}
          />
          <span id={`${inputId}-hint`} className={tooBig ? 'error-text' : 'field-hint'} data-testid="custom-css-size">
            {formatBytes(bytes)}
            {maxBytes > 0 ? ` von ${formatBytes(maxBytes)}` : ''} · Tab rückt ein, Umschalt+Tab verlässt das Feld
          </span>
        </div>
        <div className="field">
          <span className="label">Vorschau</span>
          <CssSampleCard />
        </div>
      </div>
      {changed && (
        <div className="lf-notice" role="status" data-testid="custom-css-sanitized">
          Einige Teile wurden aus Sicherheitsgründen entfernt oder ersetzt. Angewendet wird:
          <pre className="lf-code-block" data-testid="custom-css-clean">
            {clean.trim() || '(leer)'}
          </pre>
        </div>
      )}
      <label className="switch-row">
        <input type="checkbox" className="switch" checked={live} onChange={(e) => setLive(e.target.checked)} data-testid="custom-css-live" />
        <span>Live-Vorschau in der ganzen App</span>
      </label>
      <div className="row">
        <span className="spacer" />
        <button type="button" className="btn btn-ghost" onClick={() => setDraft(saved)} disabled={!dirty || busy} data-testid="custom-css-reset">
          Verwerfen
        </button>
        <button type="button" className="btn btn-primary" onClick={() => void apply()} disabled={!dirty || busy || tooBig} data-testid="custom-css-apply">
          Übernehmen
        </button>
      </div>
    </section>
  );
}

/** Beispiel-Nachrichtenkarte für die Theme-/CSS-Vorschau (Klassen dokumentiert in TESTIDS.md). */
export function CssSampleCard({ testId = 'custom-css-preview' }: { testId?: string }) {
  return (
    <div className="lf-sample" data-testid={testId} aria-label="Beispielnachricht">
      <span className="sr-only">Beispielnachricht zur Vorschau</span>
      <div className="lf-sample-message">
        <span className="lf-sample-avatar" aria-hidden="true">
          GL
        </span>
        <div className="lf-sample-body">
          <div className="lf-sample-head">
            <span className="lf-sample-author">Glutwächterin</span>
            <span className="lf-sample-time">Heute um 21:14</span>
          </div>
          <div className="lf-sample-text">
            Wer bringt heute die Marshmallows mit? <span className="lf-sample-mention">@Funkenflug</span>
          </div>
          <div className="lf-sample-reactions">
            <span className="lf-sample-reaction" data-me="true">
              🔥 3
            </span>
            <span className="lf-sample-reaction">🌲 1</span>
          </div>
        </div>
      </div>
      <div className="lf-sample-row">
        <button type="button" className="btn btn-primary btn-sm" tabIndex={-1}>
          Akzent
        </button>
        <button type="button" className="btn btn-sm" tabIndex={-1}>
          Standard
        </button>
        <span className="lf-sample-link">Ein Link</span>
      </div>
    </div>
  );
}
